import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Question, InterviewResult } from '@/contexts/InterviewContext'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ChevronDown, ChevronRight, Download, Search, User } from 'lucide-react'
import { fetchAllRows } from '@/utils/supabasePagination'
import * as XLSX from 'xlsx'

interface QuestionAnalyticsProps {
  question: Question
  results: InterviewResult[]
}

interface AnswerStat {
  optionId: string
  optionText: string
  optionLabel: string
  count: number
  percentage: number
}

interface AnswerDistribution {
  name: string
  value: number
  color: string
}

interface AssignedUser {
  employee_test_id: string
  employee_id: string
  personel: string
  sicil: string
  answer: string | null
  is_correct: boolean | null
  choiceLabel: string | null
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d']

const QuestionAnalytics: React.FC<QuestionAnalyticsProps> = ({ question, results }) => {
  const [showUsers, setShowUsers] = useState(false)
  const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([])
  const [filteredUsers, setFilteredUsers] = useState<AssignedUser[]>([])
  const [sicilFilter, setSicilFilter] = useState('')
  const [loadingUsers, setLoadingUsers] = useState(false)

  // Fetch assigned users when showUsers is toggled
  useEffect(() => {
    if (!showUsers) return

    fetchAssignedUsers()
  }, [showUsers, question.id])

  // Filter users based on sicil search
  useEffect(() => {
    if (sicilFilter.trim() === '') {
      setFilteredUsers(assignedUsers)
    } else {
      const filtered = assignedUsers.filter((user) =>
        user.sicil.toLocaleLowerCase('tr').includes(sicilFilter.toLocaleLowerCase('tr'))
      )
      setFilteredUsers(filtered)
    }
  }, [sicilFilter, assignedUsers])

  const fetchAssignedUsers = async () => {
    setLoadingUsers(true)
    try {
      // Get all employee_test_ids that have answered this question
      const employeeTestIds = Array.from(
        new Set(
          results.flatMap((result) =>
            result.responses
              .filter((response) => response.questionId === question.id)
              .map(() => result.candidateId)
          )
        )
      )
      if (employeeTestIds.length === 0) {
        setAssignedUsers([])
        setFilteredUsers([])
        setLoadingUsers(false)
        return
      }

      // Fetch employee_tests with employee information
      const employeeTestsData = await fetchAllRows<any>(
        'employee_tests',
        `
          id,
          employee_id,
          employee (
            personel,
            sicil
          )
        `,
        (q) => q.in('id', employeeTestIds)
      )

      // Fetch answers for this question
      const answersData = await fetchAllRows<any>(
        'employee_answers',
        'employee_test_id, choice_id, is_correct',
        (q) => q.in('employee_test_id', employeeTestIds).eq('question_id', question.id)
      )

      // Create a map of answers by employee_test_id
      const answersMap = new Map(
        answersData?.map((answer) => [answer.employee_test_id, answer]) || []
      )

      // Combine the data
      const users: AssignedUser[] = (employeeTestsData || []).map((et: any) => {
        const answer = answersMap.get(et.id)
        const choice = question.options?.find((opt) => opt.id === answer?.choice_id?.toString())

        return {
          employee_test_id: et.id,
          employee_id: et.employee_id,
          personel: et.employee?.personel || 'N/A',
          sicil: et.employee?.sicil || 'N/A',
          answer: choice?.text || null,
          is_correct: answer?.is_correct ?? null,
          choiceLabel: choice?.label || null,
        }
      })

      // Sort by sicil
      users.sort((a, b) => a.sicil.localeCompare(b.sicil))

      setAssignedUsers(users)
      setFilteredUsers(users)
    } catch (error) {
      console.error('Error fetching assigned users:', error)
    } finally {
      setLoadingUsers(false)
    }
  }

  // Calculate statistics for this question
  const calculateStats = () => {
    // Get all responses for this question
    const responses = results.flatMap((result) =>
      result.responses.filter((response) => response.questionId === question.id)
    )

    const totalResponses = responses.length

    // This check is important!
    if (totalResponses === 0 || !question.options) {
      return {
        totalResponses: 0,
        correctCount: 0,
        incorrectCount: 0,
        correctPercentage: 0,
        incorrectPercentage: 0,
        answerStats: [],
        distribution: [],
      }
    }

    const correctCount = responses.filter((r) => r.isCorrect).length
    const incorrectCount = totalResponses - correctCount
    const correctPercentage = Math.round((correctCount / totalResponses) * 100)
    const incorrectPercentage = 100 - correctPercentage

    // Calculate statistics for each option
    const answerStats: AnswerStat[] = question.options.map((option) => {
      const optionCount = responses.filter((r) => r.choiceId === option.id).length

      return {
        optionId: option.id,
        optionText: option.text,
        optionLabel: option.label, // <-- Pass the label through
        count: optionCount,
        percentage: Math.round((optionCount / totalResponses) * 100),
      }
    })

    // Create distribution data for pie chart
    const distribution: AnswerDistribution[] = [
      {
        name: 'Doğru',
        value: correctCount,
        color: '#4ade80', // green
      },
      {
        name: 'Yanlış',
        value: incorrectCount,
        color: '#f87171', // red
      },
    ]

    return {
      totalResponses,
      correctCount,
      incorrectCount,
      correctPercentage,
      incorrectPercentage,
      answerStats,
      distribution,
    }
  }

  const stats = calculateStats()

  // Prepare data for bar chart
  const barData = stats.answerStats.map((stat, index) => ({
    name: stat.optionLabel, // <-- Use the readable label for the chart axis
    count: stat.count,
    percentage: stat.percentage,
    fill: COLORS[index % COLORS.length],
  }))

  const handleDownloadExcel = () => {
    // Format data for Excel
    const dataToExport = filteredUsers.map((user) => {
      let durum = 'Cevaplanmadı'
      if (user.is_correct === true) durum = 'Doğru'
      if (user.is_correct === false) durum = 'Yanlış'

      return {
        'Sicil No': user.sicil,
        'Ad Soyad': user.personel,
        'Verilen Cevap Seçeneği': user.choiceLabel ? user.choiceLabel.toUpperCase() : '-',
        'Verilen Cevap Metni': user.answer || 'Cevaplanmadı',
        Durum: durum,
      }
    })

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(dataToExport)

    // Adjust column widths
    const wscols = [
      { wch: 15 }, // Sicil
      { wch: 25 }, // Ad Soyad
      { wch: 15 }, // Seçenek
      { wch: 40 }, // Metin
      { wch: 15 }, // Durum
    ]
    worksheet['!cols'] = wscols

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Atananlar')

    // Generate filename with timestamp
    const fileName = `Soru_Analiz_${new Date().toISOString().slice(0, 10)}.xlsx`

    XLSX.writeFile(workbook, fileName)
  }

  return (
    <Card className="mb-8">
      <CardContent className="p-6">
        {stats.totalResponses > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-500 mb-1">Toplam Cevap</p>
                <p className="text-2xl font-bold">{stats.totalResponses}</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-green-600 mb-1">Doğru Cevaplar</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats.correctCount} <span className="text-sm">(%{stats.correctPercentage})</span>
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="text-sm text-red-600 mb-1">Yanlış Cevaplar</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.incorrectCount}{' '}
                  <span className="text-sm">(%{stats.incorrectPercentage})</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-6">
              <div>
                <h3 className="font-medium mb-4 text-center">Yanıt Dağılımı</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.distribution}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {stats.distribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`${value} yanıt`, '']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div>
                <h3 className="font-medium mb-4 text-center">Yanıt Dağılımı (Seçenekler)</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={barData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => [`${value} yanıt`, '']} />
                      <Legend />
                      <Bar dataKey="count" name="Yanıt Sayısı" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-medium mb-2">Cevap Detayları</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                {stats.answerStats.map((stat) => (
                  <div
                    key={stat.optionId}
                    className="border rounded p-3 flex justify-between items-center"
                  >
                    <div>
                      <span className="font-medium">{stat.optionLabel.toUpperCase()}:</span>{' '}
                      {stat.optionText}
                    </div>
                    <div className="text-sm font-medium">
                      {stat.count} <span className="text-gray-500">(%{stat.percentage})</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Assigned Users Section */}
            <div className="mt-6 border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowUsers((s) => !s)
                }}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="font-medium">Atanan Çalışanlar</span>
                </div>
                {showUsers ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>

              {showUsers && (
                <div className="mt-4 space-y-4" onClick={(e) => e.stopPropagation()}>
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    </div>
                  ) : (
                    <>
                      {/* TOOLBAR CONTAINER */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="relative flex items-center gap-2 w-full sm:flex-1 sm:max-w-md">
                          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                          <Input
                            placeholder="Sicil numarasına göre ara..."
                            value={sicilFilter}
                            onChange={(e) => setSicilFilter(e.target.value)}
                            className="w-full pl-9"
                          />
                          {sicilFilter && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSicilFilter('')}
                              className="absolute right-1 top-1 h-7 px-2"
                            >
                              Temizle
                            </Button>
                          )}
                        </div>

                        {/* EXCEL BUTTON */}
                        {filteredUsers.length > 0 && (
                          <Button
                            variant="outline"
                            onClick={handleDownloadExcel}
                            className="w-full sm:w-auto gap-2 text-green-700 hover:text-green-800 border-green-200 hover:border-green-300 hover:bg-green-50"
                          >
                            <Download className="h-4 w-4" />
                            Excel
                          </Button>
                        )}
                      </div>

                      {/* Users List */}
                      {filteredUsers.length > 0 ? (
                        <div className="border rounded-lg overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Sicil
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Personel
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Verilen Cevap
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Durum
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {filteredUsers.map((user) => (
                                <tr key={user.employee_test_id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {user.sicil}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                    {user.personel}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700">
                                    {user.answer ? (
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline">
                                          {user.choiceLabel?.toUpperCase()}
                                        </Badge>
                                        <span className="truncate max-w-xs">{user.answer}</span>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic">Cevaplanmadı</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                                    {user.is_correct === null ? (
                                      <Badge variant="outline" className="bg-gray-100">
                                        Cevaplanmadı
                                      </Badge>
                                    ) : user.is_correct ? (
                                      <Badge className="bg-green-100 text-green-800 border-green-200">
                                        ✓ Doğru
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-red-100 text-red-800 border-red-200">
                                        ✗ Yanlış
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          {sicilFilter
                            ? 'Arama kriterine uygun çalışan bulunamadı.'
                            : 'Bu soruya atanan çalışan bulunmamaktadır.'}
                        </div>
                      )}

                      {/* Summary */}
                      {filteredUsers.length > 0 && (
                        <div className="text-sm text-gray-500 text-right">
                          Toplam {filteredUsers.length} çalışan gösteriliyor
                          {sicilFilter &&
                            ` (${assignedUsers.length} çalışan arasından filtrelendi)`}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">Bu soru için henüz yanıt bulunmamaktadır.</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default QuestionAnalytics
